/**
 * Plano B do orbe, sem WebGL: canvas 2D com a mesma paleta e o mesmo enquadramento do shader —
 * esfera com diâmetro de 58% do lado, halo menta e, ouvindo e falando, três ondas de vidro menta
 * em volta, que somem antes da borda. Os mesmos parâmetros mexem nele: aura e abertura nas ondas,
 * ondulação no tremor do contorno, fluxo na velocidade das manchas, recolher trazendo tudo para
 * junto da esfera.
 */

import type { ParametrosDoOrbe } from './sinal';

const RAIO = 0.58;
/** Até onde halo e ondas podem ir, em frações da metade do lado: nada encosta na borda. */
const ALCANCE = 0.9;
const PASSOS_DA_ONDA = 96;

/** Afastamento de cada onda (a maior atrás) e o peso de cada harmônico dela. */
const ONDAS = [
    { afasta: 0.3, fase: 0, alfa: 0.8 },
    { afasta: 0.2, fase: 2.1, alfa: 1 },
    { afasta: 0.11, fase: 4.2, alfa: 1 },
];

function desenharOndas(pincel: CanvasRenderingContext2D, cx: number, cy: number, raio: number, metade: number, lado: number, p: ParametrosDoOrbe, tempoS: number): void {
    const forca = Math.min(1, p.aura / 0.35);
    if (forca <= 0.01) return;
    const perto = 1 - 0.65 * p.recolher;
    const abre = (0.05 + 0.3 * p.abertura) * p.aura * perto;
    ONDAS.forEach((o, i) => {
        const base = raio * (1 + p.aura * perto * o.afasta);
        pincel.beginPath();
        for (let k = 0; k <= PASSOS_DA_ONDA; k += 1) {
            const a = (k / PASSOS_DA_ONDA) * Math.PI * 2;
            const forma = Math.sin(a * 3 + tempoS * (0.5 + i * 0.2) + o.fase) * 0.6 + Math.sin(a * 5 - tempoS * 0.4 + i) * 0.4;
            const tremor = raio * p.ondulacao * 0.012 * Math.sin(a * 17 + tempoS * 6);
            const r = Math.min(metade * ALCANCE, base + raio * abre * forma + tremor);
            const x = cx + Math.cos(a) * r;
            const y = cy + Math.sin(a) * r;
            if (k === 0) pincel.moveTo(x, y);
            else pincel.lineTo(x, y);
        }
        pincel.closePath();
        pincel.fillStyle = `rgba(91, 255, 208, ${String(0.1 * forca * o.alfa)})`;
        pincel.fill();
        pincel.strokeStyle = `rgba(37, 230, 160, ${String(0.4 * forca * o.alfa)})`;
        pincel.lineWidth = Math.max(1, lado / 420);
        pincel.stroke();
    });
}

export function desenharOrbe2D(
    pincel: CanvasRenderingContext2D, largura: number, altura: number, p: ParametrosDoOrbe, tempoS: number, relogioS: number,
): void {
    const lado = Math.min(largura, altura);
    const metade = lado / 2;
    const cx = largura / 2;
    const cy = altura / 2;
    const raio = metade * RAIO * (1 + 0.008 * Math.sin(relogioS * 0.9) + p.deformacao * 0.5 * Math.sin(tempoS * 0.7));

    pincel.clearRect(0, 0, largura, altura);

    const halo = pincel.createRadialGradient(cx, cy, raio * 0.9, cx, cy, Math.min(metade * ALCANCE, raio * 1.55));
    halo.addColorStop(0, `rgba(91, 255, 208, ${String(0.38 * p.halo)})`);
    halo.addColorStop(1, 'rgba(91, 255, 208, 0)');
    pincel.fillStyle = halo;
    pincel.fillRect(0, 0, largura, altura);

    desenharOndas(pincel, cx, cy, raio, metade, lado, p, tempoS);

    pincel.save();
    pincel.beginPath();
    pincel.arc(cx, cy, raio, 0, Math.PI * 2);
    pincel.clip();

    const base = pincel.createRadialGradient(cx + raio * 0.08, cy - raio * 0.05, raio * 0.05, cx, cy, raio);
    base.addColorStop(0, '#041B19');
    base.addColorStop(0.45, '#07372F');
    base.addColorStop(0.82, '#25F8AC');
    base.addColorStop(1, '#5BFFD0');
    pincel.fillStyle = base;
    pincel.fillRect(cx - raio, cy - raio, raio * 2, raio * 2);

    /* Volutas: duas manchas menta claro e uma de tinta funda girando devagar. */
    const giro = tempoS * (0.6 + p.fluxo);
    const manchas = [
        { rgb: '91, 255, 208', raio: 0.55, velocidade: 0.21, fase: 0, alfa: 0.5 },
        { rgb: '37, 248, 172', raio: 0.45, velocidade: -0.17, fase: 2.4, alfa: 0.45 },
        { rgb: '4, 27, 25', raio: 0.4, velocidade: 0.12, fase: 4.4, alfa: 0.35 },
    ];
    for (const m of manchas) {
        const afastamento = raio * 0.4 * (1 - p.recolher * 0.5);
        const mx = cx + Math.cos(giro * m.velocidade + m.fase) * afastamento;
        const my = cy + Math.sin(giro * m.velocidade * 1.3 + m.fase) * afastamento;
        const mancha = pincel.createRadialGradient(mx, my, 0, mx, my, raio * m.raio);
        mancha.addColorStop(0, `rgba(${m.rgb}, ${String(m.alfa)})`);
        mancha.addColorStop(1, `rgba(${m.rgb}, 0)`);
        pincel.fillStyle = mancha;
        pincel.fillRect(cx - raio, cy - raio, raio * 2, raio * 2);
    }

    const deBaixo = pincel.createRadialGradient(cx, cy + raio * 0.95, raio * 0.1, cx, cy + raio * 0.95, raio * 0.9);
    deBaixo.addColorStop(0, 'rgba(91, 255, 208, 0.6)');
    deBaixo.addColorStop(1, 'rgba(91, 255, 208, 0)');
    pincel.fillStyle = deBaixo;
    pincel.fillRect(cx - raio, cy - raio, raio * 2, raio * 2);

    const reflexo = pincel.createRadialGradient(cx - raio * 0.38, cy - raio * 0.45, 0, cx - raio * 0.38, cy - raio * 0.45, raio * 0.32);
    reflexo.addColorStop(0, 'rgba(244, 247, 245, 0.6)');
    reflexo.addColorStop(1, 'rgba(244, 247, 245, 0)');
    pincel.fillStyle = reflexo;
    pincel.fillRect(cx - raio, cy - raio, raio * 2, raio * 2);

    if (p.brilho < 1) {
        pincel.fillStyle = `rgba(17, 26, 24, ${String((1 - p.brilho) * 0.6)})`;
        pincel.fillRect(cx - raio, cy - raio, raio * 2, raio * 2);
    }
    pincel.restore();

    pincel.beginPath();
    pincel.arc(cx, cy, raio - 0.5, 0, Math.PI * 2);
    pincel.strokeStyle = 'rgba(91, 255, 208, 0.7)';
    pincel.lineWidth = Math.max(1, lado / 360);
    pincel.stroke();
}
